import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Check, X, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Client, DbEvent } from '../types';
import { calcBalance, calcTotal, fmtMoney, fmtDate } from '../lib/pricing';

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [events, setEvents] = useState<DbEvent[]>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Client>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      supabase.from('clients').select('*').eq('id', id).single(),
      supabase.from('events').select('*').eq('client_id', id).order('event_date', { ascending: false }),
    ]).then(([clientRes, eventsRes]) => {
      setClient(clientRes.data);
      setForm(clientRes.data ?? {});
      setEvents(eventsRes.data ?? []);
      setLoading(false);
    });
  }, [id]);

  const saveEdit = async () => {
    if (!id) return;
    setSaving(true);
    const { data } = await supabase
      .from('clients')
      .update({ name: form.name, phone: form.phone, email: form.email, instagram: form.instagram, notes: form.notes })
      .eq('id', id)
      .select()
      .single();
    setClient(data);
    setEditing(false);
    setSaving(false);
  };

  const totalPaid = events.reduce((s, e) => s + (e.amount_paid ?? 0), 0);
  const totalOwed = events.reduce((s, e) => s + Math.max(0, calcBalance(e)), 0);

  const statusColor = (s: string) =>
    ({ upcoming: 'text-blue-600 bg-blue-50', completed: 'text-green-600 bg-green-50', cancelled: 'text-gray-500 bg-gray-100' }[s] ?? 'text-gray-500 bg-gray-100');

  if (loading) return (
    <div className="flex justify-center items-center py-24">
      <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!client) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center text-gray-500">Client not found.</div>
  );

  const field = (label: string, key: keyof Client, type = 'text') => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {editing ? (
        <input
          type={type}
          value={(form[key] as string) ?? ''}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
        />
      ) : (
        <p className="text-sm text-gray-900">{(client[key] as string) || <span className="text-gray-400">—</span>}</p>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} /> Clients
      </button>

      {/* Profile card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
        <div className="flex items-start justify-between mb-4">
          <h1 className="font-serif text-xl font-bold text-gray-900">{client.name}</h1>
          {editing ? (
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); setForm(client); }} className="p-2 rounded-lg hover:bg-gray-100">
                <X size={16} className="text-gray-500" />
              </button>
              <button onClick={saveEdit} disabled={saving} className="p-2 rounded-lg bg-gold-500 hover:bg-gold-600">
                <Check size={16} className="text-white" />
              </button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)} className="p-2 rounded-lg hover:bg-gray-100">
              <Edit2 size={16} className="text-gray-500" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field('Name', 'name')}
          {field('Phone', 'phone', 'tel')}
          {field('Email', 'email', 'email')}
          {field('Instagram', 'instagram')}
        </div>
        {editing ? (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea
              value={(form.notes as string) ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 resize-none"
            />
          </div>
        ) : client.notes ? (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <p className="text-sm text-gray-700">{client.notes}</p>
          </div>
        ) : null}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Total Paid</p>
          <p className="text-lg font-bold text-green-600">{fmtMoney(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Still Owed</p>
          <p className={`text-lg font-bold ${totalOwed > 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmtMoney(totalOwed)}</p>
        </div>
      </div>

      {/* Events */}
      <h2 className="font-serif text-lg font-semibold text-gray-900 mb-3">Events</h2>
      {events.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-400 text-sm">
          No events yet
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {events.map(ev => (
            <button
              key={ev.id}
              onClick={() => navigate(`/events/${ev.id}`)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{ev.event_type ?? 'Event'} · {fmtDate(ev.event_date)}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total: {fmtMoney(calcTotal(ev))} · Paid: {fmtMoney(ev.amount_paid ?? 0)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(ev.status)}`}>{ev.status}</span>
                <ChevronRight size={14} className="text-gray-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
